import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Spinner from "../../components/Shared/Spinner";
import Button from "../../components/Shared/Button";
import AddIcon from "../../components/Icons/AddIcon";
import CbqExamsTable from "../../components/Dashboard/CBQExams/CbqExamsTable";
import { GetAdminCBQExams } from "../../api/Quiz/GetAdminCBQExams";

const CbqExams = () => {
  const { courseId = "" } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryFn: () => GetAdminCBQExams({ courseId, q: search }),
    queryKey: ["admin-cbq-exams", courseId, search],
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  const exams = data?.data ?? [];

  if (isLoading && !search) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-7">CBQ Exams</h1>
      <div className="p-8 rounded-xl bg-white border-black/15 shadow">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-bold text-2xl">All CBQ Exams</h1>
          <Link to={`/dashboard/courses/add-quiz/${courseId}`}>
            <Button
              className="!h-12 w-48 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
              variant="primary"
            >
              <AddIcon className="mr-1.5" />
              Create CBQ Exam
            </Button>
          </Link>
        </div>

        <div className="flex gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search exams..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {data?.error ? (
          <p className="py-10 text-center text-red-700">
            {data.error.message || "Could not load CBQ exams"}
          </p>
        ) : isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <CbqExamsTable data={exams} courseId={courseId} />
        )}

        {!data?.error && exams.length > 0 && (
          <div className="mt-6 text-sm text-gray-600">
            Showing {exams.length}
            {data?.total ? ` of ${data.total}` : ""} exam
            {exams.length === 1 ? "" : "s"}
          </div>
        )}
      </div>
    </div>
  );
};

export default CbqExams;
